import { useNavigate } from 'react-router-dom';
import { Clock, Users, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ConversationSummary } from '@/types';

interface ConversationCardProps {
  conversation: ConversationSummary;
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

export function ConversationCard({ conversation }: ConversationCardProps) {
  const navigate = useNavigate();

  const createdAt = conversation.created_at
    ? new Date(conversation.created_at)
    : null;

  return (
    <button
      data-testid="conversation-card"
      onClick={() => navigate(`/conversations/${conversation.id}`)}
      className="w-full card p-4 text-left hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="font-medium text-gray-900 truncate" data-testid="conversation-title">
            {conversation.title}
          </h3>

          {/* Topic badge */}
          <div className="mt-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600" data-testid="conversation-topic">
              {conversation.topic}
            </span>
          </div>

          {/* Metadata */}
          <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
            {/* Speakers */}
            <div className="flex items-center gap-1" data-testid="conversation-speaker-count">
              <Users className="w-4 h-4" />
              <span>{conversation.speaker_count}</span>
            </div>

            {/* Duration */}
            {conversation.duration_seconds && (
              <div className="flex items-center gap-1" data-testid="conversation-duration">
                <Clock className="w-4 h-4" />
                <span>{formatDuration(conversation.duration_seconds)}</span>
              </div>
            )}

            {/* Date */}
            {createdAt && (
              <span className="text-gray-400" data-testid="conversation-date">
                {formatDistanceToNow(createdAt, { addSuffix: true })}
              </span>
            )}
          </div>

          {/* Speakers list */}
          {conversation.speaker_names.length > 0 && (
            <p className="mt-2 text-sm text-gray-500 truncate" data-testid="conversation-speakers">
              {conversation.speaker_names.join(', ')}
            </p>
          )}
        </div>

        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
      </div>
    </button>
  );
}
